import { ISaSmsPage } from '@interfaces/sa-sms';
import { Card, Table } from 'antd';
import { useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useSaSmsUserNames } from 'hooks/use-sa-sms-user-names';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Expected rows of marketing-api `GET /v1/internal/sms/tenants/:id/audit` (the P16 `sms_tenant_config_audit` table). */
type ConfigAuditRow = {
    id: string | number;
    actorUserId: string;
    field: string;
    oldValue: unknown;
    newValue: unknown;
    reason: string | null;
    createdAt: string;
};

const PAGE_SIZE = 25;
const show = (v: unknown) =>
    v === null || v === undefined ? '-' : typeof v === 'object' ? JSON.stringify(v) : String(v);

/** Field-level config changes by every actor (needs the `configAudit` feature). */
export const SmsConfigAuditTable: React.FC<{ tenantId: string }> = ({ tenantId }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const [page, setPage] = useState(1);
    const { data, isLoading } = useSaSmsGet<ISaSmsPage<ConfigAuditRow>>(
        `/tenants/${tenantId}/config-audit`,
        { page, pageSize: PAGE_SIZE },
    );
    const names = useSaSmsUserNames((data?.items ?? []).map((e) => e.actorUserId));
    return (
        <Card title={t('activity.configAudit')}>
            <Table<ConfigAuditRow>
                rowKey="id"
                size="small"
                loading={isLoading}
                dataSource={data?.items ?? []}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: data?.total ?? 0,
                    showSizeChanger: false,
                    onChange: setPage,
                }}
                scroll={{ x: 800 }}
            >
                <Table.Column<ConfigAuditRow>
                    title={t('credits.date')}
                    render={(_, r) => new Date(r.createdAt).toLocaleString(i18n.language)}
                />
                <Table.Column<ConfigAuditRow>
                    title={t('credits.actor')}
                    render={(_, r) => names[r.actorUserId] ?? r.actorUserId}
                />
                <Table.Column<ConfigAuditRow> title={t('activity.field')} dataIndex="field" />
                <Table.Column<ConfigAuditRow>
                    title={t('activity.oldValue')}
                    render={(_, r) => show(r.oldValue)}
                />
                <Table.Column<ConfigAuditRow>
                    title={t('activity.newValue')}
                    render={(_, r) => show(r.newValue)}
                />
                <Table.Column<ConfigAuditRow>
                    title={t('credits.reason')}
                    render={(_, r) => r.reason ?? '-'}
                />
            </Table>
        </Card>
    );
};
